"use client";

import * as React from "react";
import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { Input } from "../primitives/input";
import { PasswordInput } from "../primitives/password-input";
import { Label } from "../primitives/label";
import { cn } from "../primitives/cn";

/**
 * FormField — the only carrier for form validation errors (never Toast).
 */
export function FormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  type = "text",
  placeholder,
  autoComplete,
  className,
}: {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className={cn("flex flex-col gap-3", className)}>
          <Label htmlFor={name}>{label}</Label>
          {type === "password" ? (
            <PasswordInput
              id={name}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-invalid={fieldState.error ? true : undefined}
              {...field}
            />
          ) : (
            <Input
              id={name}
              type={type}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-invalid={fieldState.error ? true : undefined}
              {...field}
            />
          )}
          {fieldState.error?.message ? (
            <p className="text-xs text-destructive" role="alert">
              {fieldState.error.message}
            </p>
          ) : null}
        </div>
      )}
    />
  );
}
